import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Buildings, 
  Users,
  WarningCircle,
  ChartBar,
  Plus,
  ArrowRight,
  Crown,
  CheckCircle,
  Clock
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { getSuperAdminData } from '../lib/api';

interface OrgStats {
  users: number;
  properties: number;
  issues_open: number;
}

interface Organization {
  id: string;
  name?: string;
  org_number?: string;
  subscription_status?: string;
  status?: string;
  stats?: OrgStats;
}

interface Stats {
  total_organizations: number;
  total_users: number;
  total_issues: number;
  active_organizations: number;
  open_issues: number;
  subscription_plans?: {
    basic?: number;
    standard?: number;
    premium?: number;
  };
}

export default function SuperAdminDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      // Single batch request for ALL data!
      const { organizations: orgs, users, issues } = await getSuperAdminData();
      
      setOrganizations(orgs.slice(0, 5)); // Show latest 5
      
      // Filter users - only count users that belong to a real organization
      const orgIds = new Set(orgs.map(o => o.id));
      const validUsers = users.filter(u => u.organization_id && orgIds.has(u.organization_id));
      
      // Calculate stats
      setStats({
        total_organizations: orgs.length,
        total_users: validUsers.length,
        total_issues: issues.length,
        active_organizations: orgs.filter(o => o.status === 'active').length,
        open_issues: issues.filter(i => i.status === 'open' || i.status === 'in_progress').length
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusBadge = (status: string | undefined) => {
    const badges: Record<string, string> = {
      active: 'badge-success',
      trial: 'badge-warning',
      suspended: 'badge-emergency',
      cancelled: 'badge-neutral'
    };
    return badges[status || 'unknown'] || 'badge-neutral';
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-64 bg-slate-200 rounded" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => (
              <div key={i} className="h-24 bg-slate-200 rounded-md" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-amber-100 flex items-center justify-center">
            <Crown size={20} className="text-amber-600" weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Superadmin
            </h1>
            <p className="text-sm text-slate-500">{user?.email}</p>
          </div>
        </div>
        <Link to="/superadmin/organizations/new">
          <Button className="btn-press" data-testid="new-organization-button">
            <Plus size={18} className="mr-2" />
            Ny förening
          </Button>
        </Link>
      </div>

      {/* Global Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card" data-testid="stat-orgs">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center">
                <Buildings size={20} className="text-blue-600" />
              </div>
              <div>
                <div className="stat-value">{stats.total_organizations || 0}</div>
                <div className="stat-label">Föreningar</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card" data-testid="stat-active">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-green-50 flex items-center justify-center">
                <CheckCircle size={20} className="text-green-600" />
              </div>
              <div>
                <div className="stat-value">{stats.active_organizations || 0}</div>
                <div className="stat-label">Aktiva</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card" data-testid="stat-trial">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-amber-50 flex items-center justify-center">
                <Clock size={20} className="text-amber-600" />
              </div>
              <div>
                <div className="stat-value">{stats.total_issues || 0}</div>
                <div className="stat-label">Ärenden</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card" data-testid="stat-users">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-purple-50 flex items-center justify-center">
                <Users size={20} className="text-purple-600" />
              </div>
              <div>
                <div className="stat-value">{stats.total_users || 0}</div>
                <div className="stat-label">Användare totalt</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Plans */}
      {stats && stats.subscription_plans && (
        <div className="card-flat">
          <h2 className="text-base font-medium text-slate-900 mb-4">Abonnemang</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 rounded-md text-center">
              <div className="text-2xl font-semibold text-slate-900">{stats.subscription_plans.basic || 0}</div>
              <div className="text-sm text-slate-500">Basic</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-md text-center">
              <div className="text-2xl font-semibold text-blue-900">{stats.subscription_plans.standard || 0}</div>
              <div className="text-sm text-blue-700">Standard</div>
            </div>
            <div className="p-4 bg-amber-50 rounded-md text-center">
              <div className="text-2xl font-semibold text-amber-900">{stats.subscription_plans.premium || 0}</div>
              <div className="text-sm text-amber-700">Premium</div>
            </div>
          </div>
        </div>
      )}

      {/* Organizations List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-medium text-slate-900">Föreningar</h2>
          <Link to="/superadmin/organizations" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
            Visa alla
            <ArrowRight size={14} />
          </Link>
        </div>
        
        {organizations.length === 0 ? (
          <div className="card-flat">
            <div className="empty-state">
              <Buildings size={48} className="empty-state-icon" />
              <p className="text-slate-500">Inga föreningar ännu</p>
              <Link to="/superadmin/organizations/new" className="mt-4">
                <Button variant="outline" size="sm">
                  <Plus size={16} className="mr-2" />
                  Skapa första föreningen
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {organizations.slice(0, 5).map((org) => (
              <Link
                key={org.id}
                to={`/superadmin/organizations/${org.id}`}
                className="card-flat block list-item-hover"
                data-testid={`org-card-${org.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center">
                      <Buildings size={20} className="text-slate-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-slate-900">{org.name}</h3>
                      <p className="text-xs text-slate-500">{org.org_number || 'Inget org.nr'}</p>
                    </div>
                  </div>
                  <span className={getStatusBadge(org.subscription_status)}>
                    {org.subscription_status}
                  </span>
                </div>
                
                {org.stats && (
                  <div className="flex items-center gap-6 mt-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      {org.stats.users} användare
                    </span>
                    <span className="flex items-center gap-1">
                      <Buildings size={14} />
                      {org.stats.properties} fastigheter
                    </span>
                    <span className="flex items-center gap-1">
                      <WarningCircle size={14} />
                      {org.stats.issues_open} öppna ärenden
                    </span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
